import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { Box, Typography, Button, TextField, InputAdornment, Paper } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { FoodsIcon } from "@foodpilot/foods";
import { useFoodpilotContext, CREATE } from "src/context/FoodpilotContext";
import { suppliersKey } from "src/api/client-api/company/useGetSuppliers";
import { CompanyListContextProvider, useCompanyListContext } from "../Company/CompanyListContext";
import { CompanyGrid } from "../Company/CompanyGrid";
import { CompanyModal } from "../Company/CompanyModal";
import { useQueryClient } from "react-query";
import { canAdministerSuppliers } from "./supplierAdministration";

type ProvidersListProps = {
    isProvider?: boolean;
};

const ProvidersListContent: FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { user } = useFoodpilotContext();
    const queryClient = useQueryClient();
    const canCreateSupplier = canAdministerSuppliers(user.trustedUser, CREATE);

    const {
        searchCompany: searchProvider,
        setSearchCompany: setSearchProvider,
        showCreateModal,
        setShowCreateModal,
    } = useCompanyListContext();

    const handleCreateProvider = () => {
        setShowCreateModal(true);
    };

    const handleModalSuccess = () => {
        queryClient.invalidateQueries([suppliersKey], { exact: false });
        setShowCreateModal(false);
    };

    return (
        <Box
            sx={{
                m: "0",
                p: "48px !important",
                flexGrow: 1,
                overflow: "auto",
            }}
        >
            <Paper
                elevation={0}
                sx={{
                    width: "100%",
                    border: "1px solid #E7E6E4",
                    borderRadius: "12px",
                    overflow: "hidden",
                    backgroundColor: "white",
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        padding: "28px 32px",
                        borderBottom: "1px solid #E7E6E4",
                        backgroundColor: "white",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", gap: "16px" }}>
                        <Box
                            onClick={() => navigate(-1)}
                            sx={{
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                "& svg": {
                                    strokeWidth: "2px",
                                    stroke: "currentColor",
                                },
                            }}
                        >
                            <FoodsIcon icon="arrowLeftShort" size={6} />
                        </Box>
                        <Box sx={{ marginLeft: "16px" }}>
                            <Typography variant="number-inter" sx={{ lineHeight: "1.6em", color: "#393A36" }}>
                                {t("settings.suppliers", "Suppliers")}
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {/* Search and Add Provider Section */}
                <Box
                    sx={{
                        padding: "24px 32px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: "24px",
                    }}
                >
                    {/* Search */}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                            flex: 1,
                            maxWidth: "323px",
                        }}
                    >
                        <TextField
                            placeholder="Search..."
                            value={searchProvider}
                            onChange={(e) => setSearchProvider(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ fontSize: 16, color: "#393A36" }} />
                                    </InputAdornment>
                                ),
                                sx: {
                                    borderRadius: "24px",
                                    backgroundColor: "white",
                                    border: "1px solid #E7E6E4",
                                    fontSize: "0.875rem",
                                    fontWeight: 500,
                                    color: "#A3A2A0",
                                    "& .MuiOutlinedInput-notchedOutline": {
                                        border: "none",
                                    },
                                    "&:hover .MuiOutlinedInput-notchedOutline": {
                                        border: "none",
                                    },
                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                        border: "none",
                                    },
                                    height: "40px",
                                    pl: 2.5,
                                    pr: 1.5,
                                },
                            }}
                            sx={{ width: "100%" }}
                        />
                    </Box>

                    {canCreateSupplier && (
                        <Button
                            variant="contained"
                            startIcon={<FoodsIcon size={2} icon="plus" />}
                            onClick={handleCreateProvider}
                            sx={(theme) => ({
                                borderRadius: "44px",
                                padding: "0px 20px",
                                height: "40px",
                                fontWeight: 600,
                                fontSize: "0.875rem",
                                textTransform: "none",
                                background: `linear-gradient(180deg, ${theme.palette.primary[500]} 0%, ${theme.palette.primary[600]} 100%)`,
                                color: "white !important",
                                boxShadow: "0px 1px 2px 0px rgba(42, 43, 39, 0.02)",
                                "&:hover": {
                                    background: `linear-gradient(180deg, ${theme.palette.primary[600]} 0%, ${theme.palette.primary[700]} 100%)`,
                                    boxShadow: "0px 1px 3px 0px rgba(42, 43, 39, 0.03)",
                                    color: "white !important",
                                },
                                "&:disabled": {
                                    background: `linear-gradient(180deg, ${theme.palette.primary[500]} 0%, ${theme.palette.primary[600]} 100%)`,
                                    opacity: 0.36,
                                    color: "white !important",
                                },
                            })}
                        >
                            {t("provider.add", "Add Provider")}
                        </Button>
                    )}
                </Box>

                {/* Providers Grid */}
                <Box sx={{ padding: "0 32px 32px 32px" }}>
                    <CompanyGrid />
                </Box>
            </Paper>

            {canCreateSupplier && (
                <CompanyModal
                    open={showCreateModal}
                    onClose={() => setShowCreateModal(false)}
                    onSuccess={handleModalSuccess}
                    company={undefined}
                    isProvider={true}
                />
            )}
        </Box>
    );
};

export const ProvidersList = (props: ProvidersListProps) => {
    return (
        <CompanyListContextProvider isProvider={props.isProvider}>
            <ProvidersListContent />
        </CompanyListContextProvider>
    );
};
